import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { useParams } from 'react-router-dom';
import * as FileSaver from 'file-saver';
import { useSnackbar } from 'notistack';
import { HistoricalResultsApis } from 'hooks/apis/HistoricalResultsApis';
import LoadPanel from 'devextreme-react/load-panel';
import PageTitle from 'pages/components/PageTitle';
import { IResult } from 'interfaces/HistoricalResults/IResult';
import { IDocument } from 'interfaces/HistoricalResults/IDocument';
import ResultDetails from './components/ResultDetails/ResultDetails';
import ProcedureDetails from './components/ProcedureDetails/ProcedureDetails';
import './ViewHistoricalResults.scss';

interface IDocumentGroup {
  Name: string;
  Documents: IDocument[];
}

const ViewHistoricalResults = () => {
  const { regtid, branch } = useParams();
  const { getResults, downloadReport } = HistoricalResultsApis();
  const { enqueueSnackbar } = useSnackbar();
  const [results, setResults] = useState<IResult>(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [showDocuments, setShowDocuments] = useState(false);

  const setDownloadErrorSnackbar = useCallback(
    (msg) => {
      enqueueSnackbar && enqueueSnackbar(msg || 'error', { variant: 'error' });
    },
    [enqueueSnackbar]
  );

  const sortedDocuments = useMemo<IDocumentGroup[]>(() => {
    if (!results?.Documents && results?.Documents.length === 0) {
      return null;
    }

    const documentGroups = [...new Set(results?.Documents?.map((doc) => doc.Type))];
    const sortedDocs = [];
    documentGroups.map((group) => {
      const docGroup = {
        Name: group,
        Documents: results?.Documents?.filter((f) => f.Type === group),
      };
      sortedDocs.push(docGroup);
    });

    return sortedDocs;
  }, [results?.Documents]);

  useEffect(() => {
    getResults(regtid, branch).then((data) => {
      if (!data.Message) {
        setResults(data);
      } else {
        // Do something with the error message on the page
      }
      setDataLoading(false);
    });
  }, [regtid, branch, getResults]);

  const downloadFile = useCallback(
    (document: IDocument) => {
      downloadReport(document)
        .then((response) => {
          FileSaver.saveAs(response, document.Filename);
        })
        .catch((error) => setDownloadErrorSnackbar(`Download Failed: ${error}`));
    },
    [downloadReport, setDownloadErrorSnackbar]
  );

  return (
    <>
      <PageTitle pageTitle={`${regtid} | ${branch}`} />

      <LoadPanel height={100} width={250} message="Loading Results" visible={dataLoading} />

      {!dataLoading && !results ? (
        <Box>No Results Available</Box>
      ) : !dataLoading ? (
        <>
          <Box className="viewHistoricalResults">
            {results?.ResultDetails.StatCd != 3 ? (
              <Box className="incompleteMessage">
                Please note: The information below is not yet complete - results are presented as unformatted data. All results are subject
                to change.
              </Box>
            ) : null}

            <ResultDetails
              resultDetails={results?.ResultDetails}
              setShowDocuments={setShowDocuments}
              hasDocuments={results?.Documents?.length > 0}
            />
            {results?.Procedures.length > 0 ? (
              <>
                <ProcedureDetails
                  procedures={results?.Procedures}
                  resultDetails={results?.ResultDetails}
                  resultIncrement={results?.Increment}
                />
                <p>* Indicates Tests not covered by NATA</p>
                {results?.ReportComments ? (
                  <Box className="commentsBlock">
                    <pre>{results?.ReportComments}</pre>
                  </Box>
                ) : null}
              </>
            ) : (
              <p>No Procedures Available</p>
            )}
          </Box>

          <Dialog open={showDocuments} onClose={() => setShowDocuments(false)}>
            <DialogTitle>Available Documents</DialogTitle>
            <DialogContent>
              <Box className="documents">
                {sortedDocuments?.map((group) => (
                  <Box key={group.Name}>
                    <h2>{group.Name}</h2>
                    {group?.Documents?.map((doc) => (
                      <Button key={`${doc?.Filename}`} onClick={() => downloadFile(doc)}>
                        {doc.Filename}
                      </Button>
                    ))}
                  </Box>
                ))}
              </Box>
            </DialogContent>
          </Dialog>
        </>
      ) : null}
    </>
  );
};

export default ViewHistoricalResults;
